import _ from 'lodash'
import isEmpty from 'lodash/isEmpty'

import type { AxisNames } from '.'

export class DimensionsGenerator {
  public generator: (object: any) => any
  public formatter: (object: any, key?: string) => any

  /**
   * DimensionsGenerator - Use this class in order to provide functions that generate the data structure
   * and maps the result to the correct chart type.
   *
   * @constructor
   * @param generate - This function that generates and returns the data structure for a chart.
   * @param formatter - This function that prepare the data structure for its chart type.
   */
  constructor(generator: (object: any, key?: string) => any, formatter: (object: any) => any) {
    this.generator = generator
    this.formatter = formatter
  }
}

export default class Dimensions {
  public data: Array<Record<string, any>>
  public axis
  public dimensionGenerator: DimensionsGenerator[]

  /**
   * Dimensions - This class encapsulates the logic that generates the data structure and
   * map the data in order to use it with a chart.
   *
   * @constructor
   * @param data - Provide the raw data from report content controller
   * @param axis - Provide the X, Y and Group by keys
   * @param dimensionGenerator - Provide an tuple with a data generator to generate a data structure
   * for (X axis and Y axis), and another one to generate group the data,
   */
  constructor(
    data: Array<Record<string, any>>,
    axis: AxisNames,
    dimensionGenerator: DimensionsGenerator[]
  ) {
    this.data = data
    this.axis = axis
    this.dimensionGenerator = dimensionGenerator
  }

  /**
   * call - This method returns the data structure for a chart.
   *
   * @method
   */
  call() {
    return this.hasThirdDimension()
      ? this.calculateThreeDimensions()
      : this.calculateTwoDimensions()
  }

  /**
   * call - This method returns a true if the X, Y and groupBy keys are not null,
   * on the other hand it will return false.
   *
   * @method
   */
  private hasThirdDimension() {
    const objectWithoutUndefined = Object.fromEntries(
      Object.entries(this.axis).filter(([_, v]) => !isEmpty(v))
    )

    return Object.keys(objectWithoutUndefined).length === 3
  }

  /**
   * calculateTwoDimensions - This method returns a data structure without group the data.
   *
   * @method
   */
  private calculateTwoDimensions() {
    const dimensionGenerator = this.dimensionGenerator[0]

    const processedData = this.data.map(dimensionGenerator!.generator)
    return dimensionGenerator!.formatter(processedData)
  }

  /**
   * calculateThreeDimensions - This method returns a data structure and group the data.
   *
   * @method
   */
  private calculateThreeDimensions() {
    const { groupBy } = this.axis
    const groupedData = _.groupBy(this.data, groupBy)

    const dimensionGenerator = this.dimensionGenerator[1]

    const result = _.map(groupedData, dimensionGenerator?.generator)
    return dimensionGenerator!.formatter(result)
  }
}
